if(!customElements.get('price-slider')) {
    customElements.define('price-slider', class extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback(){
            // Logic to update slider UI
            this.minThumb = this.querySelector('[data-price-slider-thumb-min]');
            this.maxThumb = this.querySelector('[data-price-slider-thumb-max]');
            this.minThumbBubble = this.minThumb.querySelector('span');
            this.maxThumbBubble = this.maxThumb.querySelector('span');
            this.minInput = this.querySelector('input.input-min');
            this.maxInput = this.querySelector('input.input-max');
            this.track = this.querySelector('[data-price-slider-track]');
            this.range = this.querySelector('[data-price-slider-range]');

            this.inputMax = this.querySelector('input.input-max');
            this.inputMin = this.querySelector('input.input-min');

            this.updateSlider();

            // subscribe
            this.updatingProductsSubscriber = PubSub.subscribe(PubSub.events.onUpdatingFacetProducts, this.disableSliderInteraction.bind(this));
            this.updatedProductsSubscriber = PubSub.subscribe(PubSub.events.onUpdatedFacetProducts, this.enableSliderInteraction.bind(this));
        }

        disconnectedCallback(){
            // unsubscribe
            if(this.updatedProductsSubscriber){
                this.updatedProductsSubscriber();
            }
            if(this.updatingProductsSubscriber){
                this.updatingProductsSubscriber();
            }
        }

        enableSliderInteraction(){
            this.setAttribute('data-disabled', 'false');
        }

        disableSliderInteraction(){
            this.setAttribute('data-disabled', 'true');
        }

        updateSlider(){
            // Range config (set as attributes or data-*)
            this.minValue = Number(this.dataset.min ?? this.dataset.min ?? 0);
            this.maxValue = Number(this.dataset.max ?? this.dataset.max ?? 1000);
            this.step = Number(this.dataset.step ?? this.dataset.step ?? 1);

            this.dragging = null; // "min" | "max"
            this.minPx = 0;
            this.maxPx = 0;

            this.onPointerDown = this.onPointerDown.bind(this);
            this.onPointerMove = this.onPointerMove.bind(this);
            this.onPointerUp = this.onPointerUp.bind(this);
            this.onMouseLeave = this.onMouseLeave.bind(this);

            this.minThumb.addEventListener("pointerdown", this.onPointerDown);
            this.maxThumb.addEventListener("pointerdown", this.onPointerDown);
            this.minThumb.addEventListener("keydown", this.onKeyDown.bind(this));
            this.maxThumb.addEventListener("keydown", this.onKeyDown.bind(this));
            this.minThumb.addEventListener("keyup", this.onKeyUp.bind(this));
            this.maxThumb.addEventListener("keyup", this.onKeyUp.bind(this));

            // "mouse out" behavior (fires when cursor leaves the component)
            this.addEventListener("mouseleave", this.onMouseLeave);

            // init positions from inputs (if present) else full range
            const initialMin = this.minInput ? Number(this.minInput.value || this.minValue) : this.minValue;
            const initialMax = this.maxInput ? Number(this.maxInput.value || this.maxValue) : this.maxValue;

            this.setFromValues(initialMin, initialMax, { emit: false });
        }

        clamp(n, a, b) {
            return Math.min(Math.max(n, a), b);
        }

        quantize(v) {
            const steps = Math.round((v - this.minValue) / this.step);
            return this.minValue + steps * this.step;
        }

        trackRect() {
            return this.track.getBoundingClientRect();
        }

        pxToValue(px) {
            const rect = this.trackRect();
            const ratio = rect.width ? px / rect.width : 0;
            const raw = this.minValue + ratio * (this.maxValue - this.minValue);
            return this.clamp(this.quantize(raw), this.minValue, this.maxValue);
        }

        valueToPx(value) {
            const rect = this.trackRect();
            const ratio = (value - this.minValue) / (this.maxValue - this.minValue || 1);
            return this.clamp(ratio * rect.width, 0, rect.width);
        }

        setUI() {
            this.minThumb.style.left = `${this.minPx}px`;
            this.maxThumb.style.left = `${this.maxPx}px`;
            this.range.style.left = `${this.minPx}px`;
            this.range.style.width = `${this.maxPx - this.minPx}px`;

            const minVal = this.pxToValue(this.minPx);
            const maxVal = this.pxToValue(this.maxPx);

            // Store values somewhere useful
            this.minThumb.dataset.value = String(minVal);
            this.maxThumb.dataset.value = String(maxVal);

            if (this.minInput) this.minInput.value = String(minVal);
            if (this.maxInput) this.maxInput.value = String(maxVal);

            // set bubble values
            this.minThumbBubble.textContent = `${minVal}`;
            this.maxThumbBubble.textContent = `${maxVal}`;
        }

        emitValues() {
            const minVal = this.pxToValue(this.minPx);
            const maxVal = this.pxToValue(this.maxPx);

            this.dispatchEvent(
                new CustomEvent("price-slider:change", {
                    bubbles: true,
                    detail: { min: minVal, max: maxVal },
                })
            );

            this.inputMax.value = String(maxVal);
            this.inputMin.value = String(minVal);

            this.inputMax?.dispatchEvent(new Event('change'));
            this.inputMin?.dispatchEvent(new Event('change'));
        }

        setFromValues(minVal, maxVal, { emit = true } = {}) {
            const minPx = this.valueToPx(this.clamp(minVal, this.minValue, this.maxValue));
            const maxPx = this.valueToPx(this.clamp(maxVal, this.minValue, this.maxValue));

            this.minPx = Math.min(minPx, maxPx);
            this.maxPx = Math.max(minPx, maxPx);

            this.setUI();
            if (emit) this.emitValues();
        }

        onPointerDown(e) {
            e.preventDefault();

            this.dragging = e.currentTarget === this.minThumb ? "min" : "max";

            e.currentTarget.setPointerCapture(e.pointerId);

            window.addEventListener("pointermove", this.onPointerMove);
            window.addEventListener("pointerup", this.onPointerUp, { once: true });
            window.addEventListener("pointercancel", this.onPointerUp, { once: true });

            // update immediately on down
            this.onPointerMove(e);
        }

        onPointerMove(e) {
            if (!this.dragging) return;

            const rect = this.trackRect();
            const x = this.clamp(e.clientX - rect.left, 0, rect.width);

            if (this.dragging === "min") {
                const maxPx = this.maxPx - 20; // 20px gap
                this.minPx = Math.min(x, maxPx);
            } else {
                const minPx = this.minPx + 20; // 20px gap
                this.maxPx = Math.max(x, minPx);
            }

            this.setUI();
        }

        onPointerUp() {
            if (!this.dragging) return;

            this.dragging = null;
            window.removeEventListener("pointermove", this.onPointerMove);

            // finalize value when drag ends
            this.emitValues();
        }

        onMouseLeave() {
            if (this.dragging) this.onPointerUp();
            else this.emitValues();
        }

        onKeyDown(e) {
            const which = e.currentTarget === this.minThumb ? "min" : "max";
            const key = e.key;

            // only stop page scroll for arrows
            if (key !== "ArrowLeft" && key !== "ArrowRight") return;
            e.preventDefault();

            const speed = 10;
            const step = this.step * speed;
            const gap = 20;

            const rect = this.trackRect();

            const curMin = this.pxToValue(this.minPx);
            const curMax = this.pxToValue(this.maxPx);

            if (which === "min") {
                let nextMin = curMin;
                if (key === "ArrowLeft") nextMin = curMin - step;
                if (key === "ArrowRight") nextMin = curMin + step;

                nextMin = this.clamp(this.quantize(nextMin), this.minValue, this.maxValue);

                // convert VALUE -> PX (no rect.left involved)
                let x = this.valueToPx(nextMin);
                x = this.clamp(x, 0, rect.width);

                const maxPx = this.maxPx - gap; // 20px gap
                this.minPx = Math.min(x, maxPx);
            } else {
                let nextMax = curMax;
                if (key === "ArrowLeft") nextMax = curMax - step;
                if (key === "ArrowRight") nextMax = curMax + step;

                nextMax = this.clamp(this.quantize(nextMax), this.minValue, this.maxValue);

                // convert VALUE -> PX (no rect.left involved)
                let x = this.valueToPx(nextMax);
                x = this.clamp(x, 0, rect.width);

                const minPx = this.minPx + gap; // ✅ was this.maxPx + 20 (wrong)
                this.maxPx = Math.max(x, minPx);
            }

            this._kbDirty = true;
            this.setUI();
        }

        onKeyUp(e) {
             if (!this._kbDirty) return;

            if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                e.preventDefault();
                this._kbDirty = false;
                this.emitValues();
            }
        }
    });
}